// React
import { useEffect, useState } from "react";
import PropTypes from "prop-types";

// Third-party
import moment from "moment";

// @tanstack/react-query
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

// MUI
import { Grid, Stack } from "@mui/material";

// Custom Hooks
import { useAuthorization } from "@/Common/hooks/Authorization/Authorization";

// Contexts
import { useMotivators } from "@/Motivators/context/Common/MotivatorsContext";
import { SelectedValueProvider } from "@/Motivators/context/AutoAlerts/SelectedValueContext";
import { MotivatorsSectionProvider, useSectionMotivators } from "@Motivators/context/Common/MotivatorsSectionContext";

// API
import { deleteAutoAlertsSetup, getAdministrationToolsOpportunityMining } from "@/Motivators/api/autoAlerts";

// Services
import actions from "@/Constants/actionsList";

// Components
import RoundedBox from "@Layouts/components/Basics/RoundedBox";
import EmptyBody from "@Layouts/components/Basics/EmptyBody";
import SpiffEntrySectionLoader from "@/Motivators/layouts/components/Skeletons/SpiffEntrySectionLoader";
import SmallActionButton from "@Layouts/components/Buttons/SmallActionButton";
import HeaderMotivatorContainer from "@Motivators/layouts/components/HeaderMotivatorContainer";
import AutoAlertsTable from "@Motivators/components/AutoAlerts/Tables/AutoAlertsTable";

// Assets
import empty from "@/assets/images/commons/empty.png";
import useCacheCleaner from "@/Common/hooks/CacheCleaner";

// Helpers
import {
	canAddItems,
	canUserEditMotivators,
	defineTypeEffetiveDate,
	determineDate,
	handleUpdateEffectiveDateTime,
	handleUpdateTitle,
	isNewItem,
	onMutateTitle,
	removeUnsaveItemInTree,
	unMaskId,
} from "@/Motivators/helpers";

// Constants
import { ID_NEW_SETUP } from "@/Motivators/constants";

function ContentRenderer(props) {
	const { isLoadingData, selectedItem, data: autoAlertSetup, current, createSetup, hasSpecialEditing } = props;
	const [selectedId, setSelectedId] = useState(null);
	// Context
	const {
		locationId,
		motivatorType,
		setSelection,
		isLoading,
		setIsLoading,
		identifiers: { title: newTitleDefault, attribute },
		mutations,
	} = useMotivators();
	const { commissionCycle } = useSectionMotivators();

	// Hooks
	const queryClient = useQueryClient();
	const { useAuth } = useAuthorization();

	const [paramsHeader, setParamsHeader] = useState({ title: newTitleDefault, from: null, to: null });

	const [loadingDateRange, setLoadingDateRange] = useState({
		from: false,
		to: false,
	});

	// Permissions
	const EDIT_OPPORTUNITY_MINING_SETUP = useAuth(actions.EDIT_OPPORTUNITY_MINING_SETUP);
	const MASTER_PERMISSION = canUserEditMotivators(hasSpecialEditing, commissionCycle?.status);

	/******************************************************* MUTATIONS *****************************************************/
	const { mutate: mutateTitle } = useMutation({
		mutationKey: ["updateAutoAlertsSetupTitle"],
		mutationFn: (title) => {
			return mutations.updateSetupTitle({
				locationId,
				title,
				listId: selectedId,
			});
		},
		onMutate: (title) => onMutateTitle(queryClient, [motivatorType + "Entry", selectedItem?._id, locationId], title),
		onError: (_error, _variables, context) => {
			queryClient.setQueryData([motivatorType + "Entry", selectedItem?._id, locationId], context.previousData);
		},
	});

	const { mutate: mutateDate } = useMutation({
		mutationKey: [motivatorType + "Entry", selectedItem?._id, locationId],
		mutationFn: (input) => {
			return mutations.updateEffectiveDate({
				locationId,
				autoAlertsSetupId: selectedId,
				effectiveDate: defineTypeEffetiveDate(input.type),
				effectiveDateValue: input.dateValue,
			});
		},
	});

	const { mutate: mutateDeleteAutoAlertSetup } = useMutation({
		mutationKey: ["deleteAutoAlertsSetup"],
		mutationFn: (input) => deleteAutoAlertsSetup(input),
	});

	/******************************************************** HANDLERS *****************************************************/
	const onEditTitleAlert = (title) => {
		handleUpdateTitle({
			titleDefault: newTitleDefault,
			paramsAction: paramsHeader,
			selectedItemId: selectedItem?._id,
			commissionCycleId: selectedItem?.commissionCycleId,
			setLoading: setIsLoading,
			mutateFunction: mutateTitle,
			createAction: createSetup,
			title,
			setParamsHeader,
		});
	};

	const onEditEffectiveDateAlert = (dateValue, type) => {
		handleUpdateEffectiveDateTime({
			titleDefault: newTitleDefault,
			paramsAction: paramsHeader,
			selectedItemId: selectedItem?._id,
			commissionCycleId: selectedItem?.commissionCycleId,
			setLoading: setLoadingDateRange,
			mutateFunction: mutateDate,
			createAction: createSetup,
			setParamsHeader,
			dateValue,
			type,
		});
	};

	const removeAutoAlertsSetup = async () => {
		const setupId = unMaskId(selectedItem?._id);
		if (setupId) {
			if (setupId === ID_NEW_SETUP) {
				const data = queryClient.getQueryData([`${motivatorType}Sidebar`, locationId]);
				const newData = removeUnsaveItemInTree(selectedItem.commissionCycleId, data, attribute, motivatorType);
				queryClient.setQueryData([`${motivatorType}Sidebar`, locationId], newData);
				setSelection(null);
			} else {
				try {
					setIsLoading(true);
					mutateDeleteAutoAlertSetup({
						locationId,
						autoAlertsSetupId: setupId,
						commissionCycleId: selectedItem.commissionCycleId,
					});
				} catch (error) {
					console.log("Error removing List", error);
				}
			}
		}
	};

	const handleDeleteItems = (ids, effectiveDate) => {
		const effectiveDateName = effectiveDate.type === "from" ? "effectiveDateFrom" : "effectiveDateTo";
		const effectiveDateValue = effectiveDate.dataTime;
		const input = {
			[effectiveDateName]: effectiveDateValue,
			items: ids,
		};
		setIsLoading(true);
		mutations.updateSetup({
			locationId,
			input,
			autoAlertsSetupId: selectedId,
		});
	};

	useEffect(() => {
		setParamsHeader({
			from: determineDate(selectedItem?.effectiveDateFrom),
			to: determineDate(selectedItem?.effectiveDateTo),
			title: selectedItem?.title,
		});

		setSelectedId(unMaskId(selectedItem?._id));
	}, [selectedItem]);

	if (isLoadingData) {
		return <SpiffEntrySectionLoader />;
	}

	if (!selectedItem) {
		return <EmptyBody image={empty} title="No Item selected" bodyHeight="510px" />;
	}

	const deleteAutoAlertButtonDisabled = !EDIT_OPPORTUNITY_MINING_SETUP || moment().isAfter(autoAlertSetup?.effectiveDateFrom);
	const toMinDate = () => {
		return autoAlertSetup?.effectiveDateFrom
			? moment(autoAlertSetup?.effectiveDateFrom).utc().add(1, "day")
			: moment(selectedItem?.commissionCycleEndDate).utc();
	};

	return (
		<Grid container spacing={5} component={"section"}>
			<Grid item xs={12} component={"header"}>
				<Stack direction="row" justifyContent={"space-between"}>
					<HeaderMotivatorContainer
						dateRange={{ from: paramsHeader.from, to: paramsHeader.to }}
						setLoadingDateRange={setLoadingDateRange}
						fromMinDateTime={moment(selectedItem?.commissionCycleStartDate).utc(false)}
						fromMaxDateTime={moment(selectedItem?.commissionCycleEndDate).utc(false)}
						toMinDateTime={toMinDate()}
						toDisabled={loadingDateRange.from || !autoAlertSetup?.effectiveDateFrom}
						loadingDateRange={loadingDateRange}
						isLoadingData={isLoadingData}
						title={paramsHeader.title}
						defaultTitle={newTitleDefault}
						onEditTitle={onEditTitleAlert}
						onEditEffectiveDate={onEditEffectiveDateAlert}
						hasEditPermission={EDIT_OPPORTUNITY_MINING_SETUP}
						onDeleteItems={handleDeleteItems}
						masterPermission={MASTER_PERMISSION}
					/>
				</Stack>
			</Grid>
			<Grid item xs={12} height={400} className="!pt-[4px]">
				<SelectedValueProvider>
					<AutoAlertsTable
						data={autoAlertSetup?.items}
						currentMonth={current}
						selectedItem={selectedItem}
						dateRange={{ from: paramsHeader.from, to: paramsHeader.to }}
						commissionCycleId={selectedItem.commissionCycleId}
						locationId={locationId}
						autoAlertsSetupId={selectedItem?._id}
						isLoading={props.loading}
						canAddItems={canAddItems(
							EDIT_OPPORTUNITY_MINING_SETUP || MASTER_PERMISSION,
							{ from: paramsHeader.from, to: paramsHeader.to },
							selectedId
						)}
						masterPermission={MASTER_PERMISSION}
					/>
				</SelectedValueProvider>
			</Grid>

			<Grid item sx={{ position: "absolute", right: "50px", bottom: "34px" }} className="special-units-list-delete">
				<SmallActionButton
					disabled={deleteAutoAlertButtonDisabled}
					buttonText={"Delete Setup"}
					buttonTextConfirm={"Confirm Delete"}
					description={""}
					onClick={removeAutoAlertsSetup}
					loading={isLoading}
				/>
			</Grid>
		</Grid>
	);
}

export default function AutoAlertsSetupSection(props) {
	const { motivatorType, queries, locationId, setTitle } = useMotivators();
	const QUERY_IDENTIFIERS = [motivatorType + "Entry", "activitiesTypeAutoAlerts", "assocWindowForAutoAlerts"];
	const { clearCache } = useCacheCleaner();
	const queryClient = useQueryClient();
	/************************************************ QUERIES *************************************************/
	const { data, isLoading: isLoadingData } = useQuery({
		queryKey: [motivatorType + "Entry", props.selectedItem?._id, locationId],
		queryFn: async () => {
			const commissionCycleId = unMaskId(props.selectedItem?._id);
			const autoAlertSetup = await queries.getBodyData({ locationId, listId: commissionCycleId });
			return {
				...autoAlertSetup,
				items: autoAlertSetup?.items?.filter((row) => {
					const rowEffectiveDate = moment(row.alertDate).utc().endOf("day");
					return rowEffectiveDate.isSameOrAfter(props?.selectedItem?.commissionCycleStartDate) || row.isNew;
				}),
			};
		},
		staleTime: Infinity,
		enabled: Boolean(locationId) && Boolean(props.selectedItem?._id) && !isNewItem(props.selectedItem?._id),
	});

	const newTitle = queryClient.getQueryData(["getProviderNameOpportunityMining", locationId]);
	useEffect(() => {
		return () => {
			clearCache(QUERY_IDENTIFIERS);
		};
	}, []);

	useEffect(() => {
		if (newTitle) {
			setTitle(`Opportunity Mining (${newTitle})`);
		}
	}, [newTitle]);

	return (
		<RoundedBox sx={{ minHeight: "630px" }} component="section">
			<MotivatorsSectionProvider>
				<ContentRenderer {...props} data={data} locationId={locationId} isLoadingData={isLoadingData} />
			</MotivatorsSectionProvider>
		</RoundedBox>
	);
}

AutoAlertsSetupSection.propTypes = {
	selectedItem: PropTypes.shape({
		_id: PropTypes.string,
		title: PropTypes.string,
		startDate: PropTypes.string,
		endDate: PropTypes.string,
		commissionCycleId: PropTypes.string,
		commissionCycleStartDate: PropTypes.string,
		commissionCycleEndDate: PropTypes.string,
	}),
	loading: PropTypes.bool,
	hasSpecialEditing: PropTypes.bool,
};

ContentRenderer.propTypes = {
	commissionCycleId: PropTypes.string,
	isLoadingData: PropTypes.bool,
	selectedItem: PropTypes.shape({
		_id: PropTypes.string,
		title: PropTypes.string,
		effectiveDateFrom: PropTypes.string,
		effectiveDateTo: PropTypes.string,
		commissionCycleId: PropTypes.string,
		commissionCycleStartDate: PropTypes.string,
		commissionCycleEndDate: PropTypes.string,
		startDate: PropTypes.string,
		endDate: PropTypes.string,
	}),
	data: PropTypes.shape({
		effectiveDateTo: PropTypes.string,
		title: PropTypes.string,
		effectiveDateFrom: PropTypes.string,
		items: PropTypes.array,
	}),
	createSetup: PropTypes.func,
	loading: PropTypes.bool,
	current: PropTypes.bool,
	hasSpecialEditing: PropTypes.bool,
};
